/**
 * Üretim, Gövde Stok ve Gövde Ön Stok sekmelerinin ortak parçaları.
 *
 * Üç sayfa da raporda aynı iskeleti kullanıyor: solda dar bir sütunda
 * "Filtreleri Temizle" düğmesi ve slicer'lar; sağda tablolar ve grafikler.
 * Sütun/satır ölçüleri Power BI sayfasındaki piksel konumlarından 24 sütunluk
 * ızgaraya çevrildi (1280 px ≈ 24 sütun).
 */
import { PANO_TABLE } from "../charts/PanoTable";
import { TEXT_KPI } from "../charts/TextKpi";

/** Kırılımlar — Özet sekmesiyle aynı. */
export const BREAKPOINTS = [
  { name: "md", maxWidth: 1024, columns: 12, rowHeight: 46 },
  { name: "sm", maxWidth: 640, columns: 4, rowHeight: 42 },
];

/**
 * Tablet ve telefon yerleşimi: kartlar verilen sırayla alt alta dizilir.
 * Masaüstündeki sol sütun dar ekranda okunmuyor; slicer'lar en üste, tam
 * genişlikte gelir.
 *
 * @param sira [[id, mdYukseklik, smYukseklik?], ...]
 * @returns id → { md, sm }
 */
export function yiginYerlesim(sira) {
  const out = {};
  let yMd = 0;
  let ySm = 0;
  for (const [id, hMd, hSm = hMd] of sira) {
    out[id] = {
      md: { x: 0, y: yMd, w: 12, h: hMd },
      sm: { x: 0, y: ySm, w: 4, h: hSm },
    };
    yMd += hMd;
    ySm += hSm;
  }
  return out;
}

/** TV modunda ilk, laptop/masaüstünde ikinci değer. */
export const tvSecici = (tvMode) => (tv, base) => (tvMode ? tv : base);

/**
 * Raporun liste slicer'ı (çoklu seçim, seçim yokken hepsi görünür).
 * Seçenekler veriden türetilir; sayfanın kaynakları zaten sayfa süzgecinden
 * geçtiği için yalnız o sayfanın değerleri listelenir.
 */
export const slicer = (id, field, label, layout, layouts, ek = {}) => ({
  id,
  type: "list",
  field,
  label,
  layout,
  layouts: layouts?.[id],
  className: "pano-slicer",
  ...ek,
});

/** Raporun "Filtreleri Temizle" düğmesi (bookmark'ı slicer'ları sıfırlıyor). */
export const temizle = (layout, layouts) => ({
  id: "temizle",
  type: "clear",
  label: "Filtreleri Temizle",
  layout,
  layouts: layouts?.temizle,
  className: "pano-temizle",
});

/**
 * Tablo kartı (charts/PanoTable.jsx).
 * @param kolonlar [{ key, label, agg?, align?, width?, format? }]
 */
export const tabloKarti = (id, baslik, source, kolonlar, layout, layouts, V, ek = {}) => ({
  id,
  source,
  layout,
  layouts: layouts?.[id],
  chart: {
    type: PANO_TABLE,
    title: baslik,
    dimensions: [],
    measures: [],
    options: {
      height: "100%",
      columns: kolonlar,
      className: "pano-tablo",
      titleStyle: { size: V(22, 15) },
      cellStyle: { size: V(15, 12) },
      headerStyle: { size: V(16, 12) },
      ...ek,
    },
  },
});

/** Metin değerli tek-değer kartı (Üretim sekmesinin sol kartları). */
export const metinKarti = (id, baslik, source, alan, layout, layouts, V) => ({
  id,
  source,
  layout,
  layouts: layouts?.[id],
  chart: {
    type: TEXT_KPI,
    dimensions: [],
    measures: [{ key: alan, label: "" }],
    options: {
      kpiTitle: baslik,
      // Başlığı gizli kartta altın şerit de çizilmez (rapordaki gibi).
      titleBar: Boolean(baslik),
      className: "pano-kpi",
      titleStyle: { size: V(15, 11) },
      valueStyle: { size: V(28, 18) },
    },
  },
});

/**
 * Zaman ekseni: rapordaki iki katlı eksen (Tarih › Saat). Kütüphanede ilk
 * boyut alt kat; `xAxisGroups` aynı tarihi taşıyan ardışık saatleri tek
 * etiket altında toplar.
 */
export const zamanEkseni = (V) => ({
  rollup: "sum",
  height: "100%",
  grid: false,
  legendPosition: "top",
  legendHeight: V(26, 20),
  xAxisGroups: true,
  xTickAngle: -90,
  xAxisHeight: V(92, 72),
  yAxisWidth: V(48, 36),
  axisStyle: { size: V(13, 9) },
  labelStyle: { size: V(14, 10) },
  legendStyle: { size: V(15, 11) },
  titleStyle: { size: V(22, 15) },
  responsive: {
    md: { axisStyle: { size: 9 }, dataLabels: false },
    sm: { axisStyle: { size: 8 }, dataLabels: false, legendStyle: { size: 9 } },
  },
});
